import { TariffCategory, TariffStatusType } from '../../shared/models/types'

type TariffStatusProps = {
  start: Date
  end: Date
  isDynamicElectricityActive: boolean
  tariffCategory: TariffCategory
}

/**
 * Function for getting tariffs status
 *  - first, we check for dynamic tariff on an installation
 *  - if dynamic tariff has end date, it is expired
 *  - if dynamic tariff is without end date, it is active
 *  - if dynamic electricity is active, all static tariffs with status active and inactive are not in use
 * @param start - start date when tariff become active
 * @param end - end date when tariff become expired
 * @param isDynamicElectricityActive - boolean for dynamic electricity indicator
 * @param tariffCategory - category of tariff as TariffCategory type
 * @returns - status as TariffStatusType
 */
export const tariffStatus = ({ start, end, isDynamicElectricityActive, tariffCategory }: TariffStatusProps): TariffStatusType => {
  const presentTime = new Date().getTime()
  const startTime = start.getTime()
  const endTime = end.getTime()
  const isDynamicTariff = tariffCategory === TariffCategory.dynamicTariff
  const isStaticTariff = tariffCategory === TariffCategory.staticTariff

  // Format dynamic tariff
  if (isDynamicTariff) {
    return TariffStatusType.active
  }

  // Format static tariff if dynamic electricity is active and make all active and inactive static tariff as not in use
  if (isStaticTariff && isDynamicElectricityActive) {
    if (!startTime && !endTime) {
      return TariffStatusType.notInUse
    }

    if (!startTime && endTime > presentTime) {
      return TariffStatusType.notInUse
    }

    if (endTime && presentTime > endTime) {
      return TariffStatusType.expired
    }

    if (startTime && presentTime > startTime) {
      return TariffStatusType.notInUse
    }

    if (startTime && presentTime < startTime) {
      return TariffStatusType.notInUse
    }
  }

  // Format static tariff properly if dynamic electricity is inactive
  if (!startTime && !endTime) {
    return TariffStatusType.active
  }

  if (!startTime && endTime > presentTime) {
    return TariffStatusType.active
  }

  if (endTime && presentTime > endTime) {
    return TariffStatusType.expired
  }

  if (startTime && presentTime > startTime) {
    return TariffStatusType.active
  }

  return TariffStatusType.inactive
}
